import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button, Form} from "react-bootstrap";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAxiosPrivateFileUpload from "../../../../hooks/useAxiosPrivateFileUpload";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import EmployeeFolderDocumentsCard from "../../common/EmployeeFolderDocumentsCard";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const TYPE_LABELS = {
  STUDENT_ID: "Legitymacja uczniowska / studencka",
  BANK_ACCOUNT_CONSENT: "Zgoda na wypłatę na rachunek osoby trzeciej",
  OTHER: "Inny dokument dla HR"
};

const EmployeeDocumentsCard = ({employeeId, requiresBankAccountConsent = false, canManage = true}) => {
  const axiosPrivate = useAxiosPrivate();
  const axiosPrivateFileUpload = useAxiosPrivateFileUpload();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [documentType, setDocumentType] = useState("STUDENT_ID");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!employeeId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosPrivate.get(`/employees/${employeeId}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać dokumentów pracownika");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, employeeId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const uploadDocument = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !employeeId) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toastError("Dozwolone są pliki PDF, JPG i PNG");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toastError("Plik nie może być większy niż 10 MB");
      return;
    }

    const formData = new FormData();
    formData.append("type", documentType);
    formData.append("file", file);
    setSaving(true);
    try {
      await axiosPrivateFileUpload.post(`/employees/${employeeId}/documents`, formData);
      await loadDocuments();
      toastSuccess("Dokument pracownika został zapisany");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się zapisać dokumentu pracownika");
    } finally {
      setSaving(false);
    }
  };

  const downloadDocument = async document => {
    try {
      const response = await axiosPrivate.get(`/employees/${employeeId}/documents/${document.id}/download`, {responseType: "blob"});
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.fileName || "dokument";
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać dokumentu pracownika");
    }
  };

  const deleteDocument = async document => {
    if (!window.confirm(`Usunąć dokument „${document.fileName}”?`)) return;
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employees/${employeeId}/documents/${document.id}`);
      await loadDocuments();
      toastSuccess("Dokument pracownika został usunięty");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się usunąć dokumentu pracownika");
    } finally {
      setSaving(false);
    }
  };

  const formatBytes = value => {
    const bytes = Number(value || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDateTime = value => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pl-PL");
  };

  if (!employeeId) {
    return <div className="resource-empty-state">Zapisz pracownika, aby dodać jego dokumenty.</div>;
  }

  return (
    <>
    <div className="resource-subcard">
      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div>
          <strong>Dokumenty przekazane przez pracownika / HR</strong>
          <div className="small text-muted mt-1">Legitymacje uczniowskie i studenckie oraz inne dokumenty przekazywane do kadr.</div>
        </div>
        {canManage && (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Select size="sm" value={documentType} onChange={event => setDocumentType(event.target.value)} disabled={saving}>
              <option value="STUDENT_ID">Legitymacja uczniowska / studencka</option>
              <option value="BANK_ACCOUNT_CONSENT">Zgoda na wypłatę na rachunek osoby trzeciej</option>
              <option value="OTHER">Inny dokument dla HR</option>
            </Form.Select>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              className="d-none"
              onChange={uploadDocument}
            />
            <Button type="button" size="sm" disabled={saving} onClick={() => fileInputRef.current?.click()}>
              Dodaj dokument
            </Button>
          </div>
        )}
      </div>
      <div className="card-body">
        <div className="small text-muted mb-3">PDF, JPG lub PNG, maksymalnie 10 MB. Dokument zapisuje się od razu po przesłaniu.</div>
        {requiresBankAccountConsent && !documents.some(document => document.type === "BANK_ACCOUNT_CONSENT") && (
          <div className="alert alert-warning py-2 mb-3">
            Rachunek bankowy należy do innej osoby. Dołącz podpisane oświadczenie pracownika o zgodzie na wypłatę wynagrodzenia na ten rachunek.
          </div>
        )}
        {loading ? (
          <div className="resource-empty-state">Ładowanie dokumentów…</div>
        ) : documents.length === 0 ? (
          <div className="resource-empty-state">Brak przesłanych dokumentów.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Rodzaj</th>
                  <th>Plik</th>
                  <th>Rozmiar</th>
                  <th>Przesłano</th>
                  <th className="text-end">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(document => (
                  <tr key={document.id}>
                    <td>{TYPE_LABELS[document.type] || document.type}</td>
                    <td>{document.fileName}</td>
                    <td>{formatBytes(document.fileSize)}</td>
                    <td>{formatDateTime(document.uploadedAt)}</td>
                    <td className="text-end text-nowrap">
                      <Button type="button" size="sm" variant="outline-primary" className="me-2" onClick={() => downloadDocument(document)}>
                        Pobierz
                      </Button>
                      {canManage && (
                        <Button type="button" size="sm" variant="outline-danger" disabled={saving} onClick={() => deleteDocument(document)}>
                          Usuń
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    <EmployeeFolderDocumentsCard employeeId={employeeId} />
    </>
  );
};

export default EmployeeDocumentsCard;
